import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSession } from '../context/SessionContext';
import { useBranding } from '../context/BrandingContext';
import './ModePickerPage.css';

function ModePickerPage() {
  const { t } = useTranslation();
  const { restaurantId } = useParams();
  const navigate = useNavigate();
  const { startSession, session } = useSession();
  const { branding, orderModes, loading } = useBranding();

  // Count enabled modes
  const enabledModes = [];
  if (orderModes.dineIn?.enabled) enabledModes.push('dine_in');
  if (orderModes.takeaway?.enabled) enabledModes.push('takeaway');
  if (orderModes.delivery?.enabled) enabledModes.push('delivery');

  // If only 1 non-dineIn mode, skip picker
  useEffect(() => {
    if (loading) return;
    if (enabledModes.length === 1 && enabledModes[0] !== 'dine_in') {
      handleSelectMode(enabledModes[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, enabledModes.length]);

  const handleSelectMode = (mode) => {
    const sessionData = {
      ...(session || {}),
      restaurantId,
      orderMode: mode,
      tableNumber: null,
    };

    startSession(sessionData);

    if (mode === 'delivery') {
      navigate(`/r/${restaurantId}/delivery-address`);
    } else if (mode === 'takeaway') {
      navigate(`/r/${restaurantId}/takeaway`);
    } else {
      // dine_in without QR — redirect to takeaway as fallback
      navigate(`/r/${restaurantId}/takeaway`);
    }
  };

  if (loading) {
    return (
      <div className="mode-picker-page">
        <div className="mode-picker-container">
          <div className="mode-picker-loading">
            <span className="material-icons spinning" aria-hidden="true">sync</span>
            <span>{t('common.loading')}</span>
          </div>
        </div>
      </div>
    );
  }

  // No modes available
  if (enabledModes.length === 0) {
    return (
      <div className="mode-picker-page">
        <div className="mode-picker-container">
          <div className="mode-picker-header">
            {branding.logoUrl && (
              <img src={branding.logoUrl} alt={branding.restaurantName} className="restaurant-logo" />
            )}
            <h1>{branding.restaurantName}</h1>
          </div>
          <div className="mode-picker-empty">
            <span className="material-icons" aria-hidden="true">info</span>
            <p>{t('modePicker.noModesAvailable')}</p>
          </div>
        </div>
      </div>
    );
  }

  // Only dine_in → show QR message
  if (enabledModes.length === 1 && enabledModes[0] === 'dine_in') {
    return (
      <div className="mode-picker-page">
        <div className="mode-picker-container">
          <div className="mode-picker-header">
            {branding.logoUrl && (
              <img src={branding.logoUrl} alt={branding.restaurantName} className="restaurant-logo" />
            )}
            <h1>{branding.restaurantName}</h1>
          </div>
          <div className="mode-picker-empty">
            <span className="material-icons" aria-hidden="true">qr_code_2</span>
            <p>{t('modePicker.dineInOnlyQr')}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mode-picker-page">
      <div className="mode-picker-container">
        <div className="mode-picker-header">
          {branding.logoUrl && (
            <img src={branding.logoUrl} alt={branding.restaurantName} className="restaurant-logo" />
          )}
          <h1>{branding.restaurantName}</h1>
          <p className="mode-picker-subtitle">{t('modePicker.subtitle')}</p>
        </div>

        <div className="mode-picker-options">
          {orderModes.dineIn?.enabled && (
            <button
              type="button"
              className="mode-option-card"
              onClick={() => handleSelectMode('dine_in')}
            >
              <div className="mode-option-icon">
                <span className="material-icons" aria-hidden="true">restaurant</span>
              </div>
              <div className="mode-option-info">
                <h3>{t('modePicker.dineIn.title')}</h3>
                <p>{t('modePicker.dineIn.description')}</p>
              </div>
              <span className="material-icons mode-option-arrow" aria-hidden="true">chevron_right</span>
            </button>
          )}

          {orderModes.takeaway?.enabled && (
            <button
              type="button"
              className="mode-option-card"
              onClick={() => handleSelectMode('takeaway')}
            >
              <div className="mode-option-icon">
                <span className="material-icons" aria-hidden="true">shopping_bag</span>
              </div>
              <div className="mode-option-info">
                <h3>{t('modePicker.takeaway.title')}</h3>
                <p>{t('modePicker.takeaway.description')}</p>
                {orderModes.takeaway.prepTimeMinutes > 0 && (
                  <span className="mode-option-detail">
                    <span className="material-icons" aria-hidden="true">schedule</span>
                    ~{orderModes.takeaway.prepTimeMinutes} min
                  </span>
                )}
              </div>
              <span className="material-icons mode-option-arrow" aria-hidden="true">chevron_right</span>
            </button>
          )}

          {orderModes.delivery?.enabled && (
            <button
              type="button"
              className="mode-option-card"
              onClick={() => handleSelectMode('delivery')}
            >
              <div className="mode-option-icon">
                <span className="material-icons" aria-hidden="true">delivery_dining</span>
              </div>
              <div className="mode-option-info">
                <h3>{t('modePicker.delivery.title')}</h3>
                <p>{t('modePicker.delivery.description')}</p>
                <span className="mode-option-detail">
                  <span className="material-icons" aria-hidden="true">schedule</span>
                  ~{orderModes.delivery.estimatedMinutes || 45} min
                  {orderModes.delivery.baseFee > 0 && (
                    <> · ${orderModes.delivery.baseFee} {t('modePicker.delivery.fee')}</>
                  )}
                </span>
              </div>
              <span className="material-icons mode-option-arrow" aria-hidden="true">chevron_right</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default ModePickerPage;
